// M3 offshore operations: actions and the hooks core/actions.js calls (assignRig, buyEquipment, resolveDecision,
// leasePlot, survey). Each action returns { ok, msg }.
import { eqDef, fieldDef, stat, yf, yearOf } from '../defs.js';
import { m3On } from './opsutil.js';
import { preAssign as preAssignImpl, afterAssign as afterAssignImpl, canDrillHorizontal } from './offshore.js';
import { buildPlatformImpl, connectPlatformImpl, installSubseaImpl, removePlatformImpl } from './platforms.js';
import { contractServiceImpl, releaseServiceImpl } from './logistics.js';
import { charterRigImpl, releaseCharterImpl, acceptCharterOutImpl, stackRigImpl } from './charters.js';
import { startEorImpl } from './eor.js';
import { joinMegaprojectImpl, transportBlockReason } from './megaprojects.js';
import { resolveStormDecision } from './storms.js';
import { setTransportImpl } from '../production.js';
import { worldPreAssign, worldCharterBlock } from '../m4/worldHooks.js'; // M4-C

const no = (msg) => ({ ok: false, msg });

export const M3_OPS_DECISION_KINDS = new Set(['storm']);
export function resolveM3OpsDecision(state, d, choice) {
  if (d.kind === 'storm') return resolveStormDecision(state, d, choice);
  return no('Unknown decision.');
}

// assignRig hooks
export function preAssign(state, rig, fieldId, plotIdx, target) {
  if (rig.charterOut) return no(`${rig.name} is out on charter until day ${rig.charterOut.untilDay}.`);
  if (rig.stacked) return no(`${rig.name} is stacked in port — reactivate it first.`);
  const r = preAssignImpl(state, rig, fieldId, plotIdx, target);
  if (r && !r.ok) return r;
  const m4 = worldPreAssign(state, rig, fieldId, plotIdx); // M4-C: moratorium, permits, containment/SEMS, sanctions, sour gas, Arctic (null before 2000)
  return m4 ?? r;
}
export const afterAssign = afterAssignImpl;

// buyEquipment hooks: a rig bought from 1946 is stamped with its build year (water envelope, §1.2).
export function onRigBought(state, rig) {
  if (!m3On(state)) return;
  rig.builtYear = yearOf(state);
  rig.offshore = !!eqDef(rig.defId)?.stats?.offshore;
}
// Routes cat 'platform' and 'logistics' purchases; returns null for every other category.
export function buyRoute(state, def, opts = {}) {
  if (def.cat === 'platform') {
    if (stat(def, 'subsea', false)) return opts.wellId ? installSubseaImpl(state, opts.wellId, def.id, opts.hostUid ?? null) : no('Install subsea trees from the well panel.');
    if (opts.fieldId == null || opts.plot == null) return no('Choose the field and plot for the platform.');
    return buildPlatformImpl(state, opts.fieldId, opts.plot, def.id);
  }
  if (def.cat === 'logistics') return contractServiceImpl(state, def.id, opts.rigUid ?? opts.platformUid ?? null);
  return null;
}

// leasePlot hook: PEMEX-style service-only fields never lease (charter-out only).
export function leaseBlock(state, fieldId) {
  const f = fieldDef(fieldId);
  const so = f?.serviceOnly;
  if (so && yf(state) >= (so.from ?? 0) - 10 && yf(state) < (so.until ?? 9999)) return `${f.name} is ${so.operator}'s: the state company never leases. Watch the charter board for rig work there.`;
  return null;
}

// survey hook: 3D seismic needs a supercomputer centre; marine surveys only offshore.
export function surveyBlock(state, fieldId, method) {
  const def = eqDef(method);
  if (!def || def.cat !== 'survey') return null;
  const b = stat(def, 'requiresBuilding', null);
  if (b && !Object.values(state.items).some((i) => i.defId === b)) return `${def.name} needs a ${eqDef(b)?.name ?? b} to process the data.`;
  if (stat(def, 'offshoreOnly', false) && !((fieldDef(fieldId)?.waterDepthMaxFt ?? fieldDef(fieldId)?.waterDepthFt ?? 0) > 0)) return `${def.name} is a marine survey.`;
  return null;
}

// setTransport hook: megaproject transports cannot be chosen before the project opens.
export function transportBlock(state, fieldId, defId) {
  if (!m3On(state)) return null;
  return transportBlockReason(state, fieldId, eqDef(defId));
}

export const buildPlatform = (state, fieldId, plot, defId) => buildPlatformImpl(state, fieldId, plot, defId);
export const connectPlatform = (state, platformUid, transportId) => connectPlatformImpl(state, platformUid, transportId, setTransportImpl);
export const installSubsea = (state, wellId, defId = 'subsea_tree', hostUid = null) => installSubseaImpl(state, wellId, defId, hostUid);
export const removePlatform = (state, platformUid) => removePlatformImpl(state, platformUid);
export const contractService = (state, defId, targetUid) => contractServiceImpl(state, defId, targetUid);
export const releaseService = (state, uid) => releaseServiceImpl(state, uid);
export const charterRig = (state, defId, fieldId, plot, opts = {}) => worldCharterBlock(state, defId, fieldId, plot) ?? charterRigImpl(state, defId, fieldId, plot, opts); // M4-C: moratorium / permits / sanctions
export const releaseCharter = (state, wellId) => releaseCharterImpl(state, wellId);
export const acceptCharterOut = (state, offerId, rigUid) => acceptCharterOutImpl(state, offerId, rigUid);
export const stackRig = (state, rigUid, on = true) => stackRigImpl(state, rigUid, on);
export const startEor = (state, fieldId, resId, method) => startEorImpl(state, fieldId, resId, method);
export const joinMegaproject = (state, id, share) => joinMegaprojectImpl(state, id, share);

// Horizontal lateral (needs the upgrade, a steerable motor and MWD on the rig): qi × (1 + lateral/1000), cost × 1.6.
export function setHorizontal(state, wellId, lateralFt) {
  const w = state.wells[wellId];
  if (!w) return no('No such well.');
  const rig = w.rigId ? state.rigs[w.rigId] : null;
  if (!rig) return no('A rig must be on the well.');
  if (w.prod) return no('The well is already completed.');
  if (!lateralFt) { w.horizontal = null; return { ok: true, msg: `${w.name}: vertical completion.` }; }
  const why = canDrillHorizontal(state, rig);
  if (why) return no(why);
  const ft = Math.max(800, Math.min(3000, Math.round(lateralFt)));
  if (w.depth < 3000 && w.target < 3000) return no('Kick off below 3,000 ft.');
  w.horizontal = { lateralFt: ft, costMult: 1.6, setDay: state.day };
  return { ok: true, msg: `${w.name}: a ${ft.toLocaleString('en-US')}-ft horizontal lateral (well cost × 1.6).` };
}

